import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { SeasonPassSignupService } from 'src/app/services/season-pass-signup.service';
import { SeasonPass, SeasonPassSignupContext, SeasonPassSignupError } from 'src/app/interfaces/abonado';
import { SeatsPanelComponent } from '../bloques/seats-panel/seats-panel.component';

/**
 * ALTA DE ABONADO en autoservicio desde la web: el mismo asistente que la
 * renovación por carné (LinkMembershipCardComponent) pero para quien NO tiene
 * abono previo, así que empieza directamente en el formulario —no hay foto de
 * carné que subir— y el socio elige lo que allí venía dado por la ficha legacy:
 * su categoría y su butaca.
 *
 * Termina llevándole al checkout del abono (/abono-checkout/:id), que es el que
 * lanza el pago con Redsys: el abono se crea pendiente de pago, igual que en la
 * renovación.
 */
@Component({
  selector: 'app-season-pass-signup',
  templateUrl: './season-pass-signup.component.html',
  styleUrls: ['./season-pass-signup.component.scss']
})
export class SeasonPassSignupComponent implements OnInit, OnDestroy {

  private static readonly FIELD_LABELS: { [key: string]: string } = {
    idCategory: 'Categoría de abonado',
    nombreApellidos: 'Nombre completo',
    nif: 'DNI/NIE',
    fechaNacimiento: 'Fecha de nacimiento',
    email: 'Email',
    emailConfirm: 'Repetir email',
    password: 'Contraseña',
    passwordConfirm: 'Repetir contraseña',
    condaccept: 'Aceptar términos y condiciones',
  };

  signupForm: FormGroup;
  // Tope del selector de fecha de nacimiento: ayer. Es solo la ayuda del
  // navegador — la validación real la hacen `pastDateValidator` y el backend.
  readonly maxBirthDate: string = new Date(Date.now() - 86400000).toISOString().substring(0, 10);

  /**
   * Temporada del alta. No va fija en el código: la resuelve el servidor y llega
   * en `context` (ver SeasonPassSignupService), y desde ahí se pasa al mapa de
   * butacas y al alta para que todo el asistente trabaje contra la misma aunque
   * el club abriera otra temporada mientras el socio rellena el formulario.
   */
  idSeason: number | null = null;

  showErrorMsg: boolean = false;
  missingFields: string[] = [];

  hidePassword: boolean = true;
  hidePasswordConfirm: boolean = true;
  hideExistingPassword: boolean = true;
  loadingContext: boolean = true;
  loadingSignup: boolean = false;
  checkingEmail: boolean = false;

  // Temporada, recinto y categorías elegibles. Sin esto no se puede dar de alta
  // a nadie (no hay categorías que ofrecer), así que su error bloquea la página.
  context: SeasonPassSignupContext | null = null;
  contextErrorMsg: string | null = null;

  // Butaca elegida en el mapa. Obligatoria: el precio del abono depende de la
  // categoría y del nivel de la butaca, así que sin ella no habría nada que
  // cobrar en el checkout.
  selectedSeatId: number | null = null;
  selectedSeatDisplay: string | null = null;
  loadingSeatMap: boolean = false;
  seatErrorMsg: string | null = null;
  // Butacas vendidas en algún evento futuro del club. No llegan al mapa como
  // ocupadas: se pueden elegir, pero al confirmarlas se avisa de que están
  // vendidas y no se quedan seleccionadas (el backend lo vuelve a comprobar).
  private soldSeatIds: number[] = [];

  // Aviso genérico a propósito: al socio no le sirve saber en qué evento está
  // vendida, y el backend solo conoce el conflicto más próximo.
  private readonly soldSeatMsg = 'Esa butaca no está disponible: ya se ha vendido una entrada para ella en un próximo evento. Elige otra en el mapa.';

  emailConfirmed: boolean = false;
  showEmailExistsPrompt: boolean = false;
  showExistingLoginError: boolean = false;
  showGenericError: boolean = false;

  // Inicio de sesión dentro del propio asistente: cuando el email ya tiene
  // cuenta, el socio se identifica con su contraseña ANTES de que se le pidan
  // sus datos personales, y así llegan prellenados con lo que ya tenga la
  // cuenta en vez de tener que teclearlos de nuevo.
  loadingExistingLogin: boolean = false;
  sessionStartedHere: boolean = false;
  // Inicio de sesión con Google: la alternativa al email + contraseña. Es la
  // ÚNICA forma de dar el alta sobre una cuenta creada con Google, porque esas
  // cuentas tienen una contraseña aleatoria que su dueño no conoce
  // (GoogleServices::findOrCreateUser), y sirve igual para quien no tiene cuenta
  // todavía: Google la crea ya activa.
  loadingGoogle: boolean = false;
  googleSession: boolean = false;
  // SocialAuthService vive en el injector raíz, así que su suscripción sobrevive
  // al componente si no se cierra a mano.
  private googleAuthSubscription: Subscription | null = null;

  // La cuenta existe pero está sin verificar (`active = false`): /auth/login la
  // rechaza con 403 antes incluso de comprobar la contraseña, así que por ahí no
  // se puede iniciar sesión ni prellenar nada. El alta sí acepta esa cuenta
  // (solo valida la contraseña), así que se continúa pidiendo los datos a mano.
  existingAccountInactive: boolean = false;
  // Mensaje tal cual lo devuelve el backend (butaca ocupada, email ya en uso,
  // categoría retirada...): son errores accionables, no un genérico.
  signupErrorMsg: string | null = null;

  // La cuenta ya tiene un abono de esta temporada y solo se puede tener uno: el
  // alta no se ha creado y no hay nada que reintentar, solo ir a verlo.
  userHasOtherPass: boolean = false;

  isLoggedIn: boolean = false;
  currentUserEmail: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthenticationService,
    private signupService: SeasonPassSignupService,
    private router: Router,
    private dialog: MatDialog,
    private socialAuthService: SocialAuthService,
  ) { }

  ngOnInit(): void {
    this.signupForm = this.formBuilder.group({
      idCategory: [null, Validators.required],
      nombreApellidos: ['', [Validators.required, Validators.minLength(4)]],
      nif: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9]{6,15}$/)]],
      // Obligatoria: es el dato que permite recalcular la categoría por edad en
      // las renovaciones de temporadas siguientes.
      fechaNacimiento: ['', [Validators.required, this.pastDateValidator()]],
      email: ['', [Validators.required, Validators.email]],
      // Email y contraseña se piden dos veces: son los datos con los que el socio
      // tendrá que entrar después a su cuenta, y una errata en cualquiera de los
      // dos deja el abono en una cuenta a la que no puede acceder.
      emailConfirm: ['', [Validators.required, Validators.email, this.matchValidator('email')]],
      password: ['', Validators.compose([
        Validators.required,
        this.patternValidator(/\d/, { hasNumber: true }),
        this.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
        this.patternValidator(/[a-z]/, { hasSmallCase: true }),
        Validators.minLength(8),
        Validators.maxLength(30)
      ])],
      passwordConfirm: ['', [Validators.required, this.matchValidator('password')]],
      existingAccountPassword: ['', Validators.required],
      condaccept: [false, [Validators.required, Validators.requiredTrue]],
    });

    // Editar el email invalida lo que supiéramos de él: hay que volver a
    // comprobarlo antes de mostrar de nuevo un campo de contraseña.
    this.signupForm.get('email')!.valueChanges.subscribe(() => {
      this.emailConfirmed = false;
      this.showEmailExistsPrompt = false;
      this.showExistingLoginError = false;
      this.existingAccountInactive = false;
      // Su validador compara con este campo, y Angular no lo reevalúa solo:
      // sin esto, corregir el email dejaría el "no coinciden" puesto (o al revés,
      // lo daría por bueno tras cambiar solo el original).
      this.revalidateConfirm('emailConfirm');
    });

    this.signupForm.get('password')!.valueChanges.subscribe(() => this.revalidateConfirm('passwordConfirm'));

    // El botón de Google entrega su usuario por `authState`, y no hay otra vía:
    // Google lo pinta dentro de su propio iframe, así que su clic no llega al
    // documento y desde aquí no se puede saber que el socio lo ha pulsado.
    //
    // `authState` es un ReplaySubject(1), así que hay que descartar su primera
    // entrega, que llega SÍNCRONA al suscribirse: esa no es un inicio de sesión
    // nuevo, es el último usuario social que hubiera en la navegación (por
    // ejemplo del login de la cabecera).
    let replayingLastState = true;
    this.googleAuthSubscription = this.socialAuthService.authState.subscribe((socialUser: SocialUser) => {
      if (replayingLastState || !socialUser || this.loadingGoogle || this.googleSession) {
        return;
      }
      this.signInWithGoogle(socialUser);
    });
    replayingLastState = false;

    this.loadContext();

    // Con sesión abierta no hay que preguntar el email ni si la cuenta existe,
    // ni pedir contraseña: el backend autoriza el alta con el propio token y la
    // asocia a la cuenta de la sesión.
    if (localStorage.getItem('jwt_token')) {
      this.authService.getUsersMe().subscribe({
        next: (res) => {
          const user = res?.data?.user || res?.data;
          this.isLoggedIn = true;
          this.currentUserEmail = user?.email || null;
          // emitEvent:false para no disparar el valueChanges de email, que
          // reiniciaría emailConfirmed justo después de darlo por bueno.
          this.signupForm.patchValue(
            {
              email: this.currentUserEmail || '',
              nombreApellidos: user?.name || '',
              nif: user?.nif || '',
              // Llega como `YYYY-MM-DD` o con hora; el input `type="date"` solo
              // admite la parte de fecha. Sigue siendo editable y obligatoria.
              fechaNacimiento: (user?.birthday || '').toString().substring(0, 10),
            },
            { emitEvent: false },
          );
          this.emailConfirmed = true;
        },
        error: () => {
          // Token caducado o inválido: se sigue por el flujo normal de alta.
          this.isLoggedIn = false;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.googleAuthSubscription?.unsubscribe();
  }

  /**
   * Temporada, recinto y categorías. Va aparte del alta porque el formulario no
   * se puede pintar sin ellas: el nombre de la temporada no está hardcodeado en
   * el front y las categorías son las que el club tenga activas.
   */
  private loadContext(): void {
    this.loadingContext = true;
    this.contextErrorMsg = null;

    this.signupService.getContext().subscribe({
      next: (context) => {
        this.loadingContext = false;
        this.context = context;
        this.idSeason = context.season?.id ?? null;
        // Con una sola categoría no hay nada que elegir: se deja puesta.
        if (context.categories?.length === 1) {
          this.signupForm.patchValue({ idCategory: context.categories[0].id });
        }
      },
      error: (err: SeasonPassSignupError) => {
        this.loadingContext = false;
        this.contextErrorMsg = err?.message
          || 'No hemos podido cargar el alta de abonados. Vuelve a intentarlo en unos minutos.';
      }
    });
  }

  /**
   * A su lista de abonos, en el perfil de usuario.
   *
   * Cambia antes el modo a "usuario": quien se da de alta lo hace como socio, no
   * como gestor, y a un admin con el modo en "organizer" (el valor por defecto)
   * lo rebota ProfileComponent al panel de gestión.
   */
  goToMyPasses(): void {
    this.authService.setUserMode('user');
    this.router.navigate(['/profile/abonos']);
  }

  /**
   * Mismo rango que valida el backend para `birthday` (posterior a 1900 y
   * anterior a hoy), para no mandar una petición que ya sabemos que va a
   * devolver 422. El input es `type="date"`, así que el valor llega como
   * `YYYY-MM-DD` o vacío.
   */
  pastDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const value = new Date(control.value);
      if (isNaN(value.getTime())) {
        return { invalidDate: true };
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return value >= today || value <= new Date('1900-01-01') ? { invalidDate: true } : null;
    };
  }

  /**
   * Campo de repetición: el valor tiene que ser idéntico al del campo original.
   *
   * Va en el propio control de repetición, no en el grupo, porque este formulario
   * se valida campo a campo (`validateFields`) según la rama del asistente: un
   * validador de grupo invalidaría el formulario entero incluso cuando el campo
   * repetido ni se pide (con sesión iniciada, o con Google).
   *
   * Vacío no es "no coincide": de eso ya avisa su `required`, y así el error de
   * desajuste no salta mientras el socio aún está escribiendo el primer carácter.
   */
  private matchValidator(originalControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const original = control.parent?.get(originalControlName);
      if (!original || !control.value) {
        return null;
      }
      return control.value === original.value ? null : { mismatch: true };
    };
  }

  // emitEvent:false: reevaluar el campo de repetición no debe volver a disparar
  // los valueChanges que lo han pedido.
  private revalidateConfirm(confirmControlName: string): void {
    this.signupForm.get(confirmControlName)!.updateValueAndValidity({ emitEvent: false });
  }

  patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      return regex.test(control.value) ? null : error;
    };
  }

  get password() { return this.signupForm.get('password')!; }
  get passwordConfirm() { return this.signupForm.get('passwordConfirm')!; }
  get emailConfirm() { return this.signupForm.get('emailConfirm')!; }

  /**
   * Los datos personales no se piden hasta saber a qué cuenta va el abono: si
   * hay sesión (la traía o la ha iniciado aquí) llegan prellenados con lo que ya
   * tuviera la cuenta, y si el email no tiene cuenta se piden en blanco.
   * Mientras esté pendiente iniciar sesión no se muestran, porque el socio los
   * rellenaría a mano para verlos sobrescritos un segundo después.
   */
  get showPersonalData(): boolean {
    if (this.isLoggedIn) {
      return true;
    }
    if (!this.emailConfirmed) {
      return false;
    }
    return !this.showEmailExistsPrompt || this.existingAccountInactive;
  }

  // Marca cada campo inválido como tocado (para que salga su error propio) y
  // recoge sus etiquetas, así el aviso puede decir exactamente qué falta.
  private validateFields(fields: string[]): boolean {
    this.missingFields = [];
    let isValid = true;
    fields.forEach((field) => {
      const control = this.signupForm.get(field);
      if (control && control.invalid) {
        control.markAsTouched();
        isValid = false;
        this.missingFields.push(SeasonPassSignupComponent.FIELD_LABELS[field] || field);
      }
    });
    return isValid;
  }

  // *ngIf solo (re)crea el nodo del aviso —y por tanto solo repite su animación
  // de entrada— en la transición false→true. Sin esto, un segundo envío fallido
  // seguido actualizaría el texto sin ninguna animación y parecería que no ha
  // pasado nada.
  private flashErrorMsg(): void {
    this.showErrorMsg = false;
    setTimeout(() => {
      this.showErrorMsg = true;
      this.scrollToAlerts();
    });
  }

  /**
   * Los avisos se pintan al principio de la tarjeta, pero el botón de alta está
   * al final de una tarjeta larga: sin esto el error aparece fuera de la
   * pantalla y lo único que se ve es desaparecer el botón.
   */
  private scrollToAlerts(): void {
    this.scrollIntoView('wizard-alerts');
  }

  private scrollIntoView(elementId: string): void {
    setTimeout(() => {
      document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /**
   * Abre el mapa del recinto para que el socio elija su butaca. El backend
   * resuelve el espacio desde la temporada (sin ids por entorno) y marca las
   * ocupadas (abonos + socios legacy sin renovar) y las bloqueadas del recinto.
   */
  openSeatMap(): void {
    if (this.loadingSeatMap) return;
    this.loadingSeatMap = true;
    this.seatErrorMsg = null;

    this.signupService.getSeats(this.idSeason ?? undefined).subscribe({
      next: (data: any) => {
        this.loadingSeatMap = false;
        if (!data?.map) {
          this.seatErrorMsg = 'No se ha podido cargar el mapa de butacas.';
          return;
        }
        this.soldSeatIds = (data.sold_seat_ids || []).map((id: any) => +id);
        const dialogRef = this.dialog.open(SeatsPanelComponent, {
          panelClass: ['info-dialog', 'not-padding', 'panel-seats'],
          data: {
            seasonPassMode: true,
            mapEvent: {
              ...data.map,
              booked_seats: data.occupied_seat_ids || [],
              reserved_seats: [],
              // Bloqueos permanentes del recinto: no están en el mapa, llegan
              // aparte del endpoint, y sin ellos el socio podría elegir una
              // butaca de PMR o retirada como si estuviera libre.
              blocked_seats: data.blocked_seat_ids || [],
            },
            // La que ya hubiera elegido, para que salga marcada al reabrir el
            // mapa. En un alta no hay butaca previa: la primera vez es null.
            currentSeatId: this.selectedSeatId,
          }
        });
        dialogRef.afterClosed().subscribe((result: any) => {
          // Sin resultado = ha cerrado el mapa sin cambiar nada (o ha confirmado
          // la que ya tenía elegida): se conserva la selección actual.
          if (!result || !result.id_seat) return;

          // Vendida en un evento del club: aunque no la ocupe ningún abonado,
          // el abonado se quedaría sin sitio en ese evento, así que no se
          // acepta la selección y se explica por qué.
          if (this.soldSeatIds.includes(+result.id_seat)) {
            this.seatErrorMsg = this.soldSeatMsg;
            return;
          }

          this.seatErrorMsg = null;
          this.selectedSeatId = result.id_seat;
          const parts: string[] = [];
          if (result.level?.name) parts.push(result.level.name);
          if (result.row != null) parts.push(`Fila ${result.row}`);
          if (result.seat != null) parts.push(`Butaca ${result.seat}`);
          this.selectedSeatDisplay = parts.join(' · ');
        });
      },
      error: (err: SeasonPassSignupError) => {
        this.loadingSeatMap = false;
        this.seatErrorMsg = err?.message || 'No se ha podido cargar el mapa de butacas.';
      }
    });
  }

  clearSeat(): void {
    this.selectedSeatId = null;
    this.selectedSeatDisplay = null;
  }

  /**
   * Averigua si el email introducido ya tiene cuenta de Citylok antes de pedir
   * ninguna contraseña, para mostrar el campo que toca (iniciar sesión, o crear
   * una contraseña nueva) en vez de los dos a la vez.
   *
   * No hay endpoint de "¿existe este email?", así que se usa el propio
   * /auth/login como sonda: busca al usuario por email ANTES de comprobar la
   * contraseña, así que una contraseña aleatoria basta para distinguir "no hay
   * cuenta" (404) de "sí la hay" (403 sin verificar / 401 contraseña incorrecta).
   */
  checkEmailAndReveal(): void {
    const emailControl = this.signupForm.get('email')!;
    // La repetición se exige ya aquí: el email es lo que decide el resto del
    // formulario, así que no tiene sentido avanzar con uno sin confirmar.
    if (emailControl.invalid || this.emailConfirm.invalid) {
      emailControl.markAsTouched();
      this.emailConfirm.markAsTouched();
      return;
    }

    this.checkingEmail = true;
    this.showGenericError = false;
    const email = emailControl!.value;
    const probePassword = Math.random().toString(36).slice(2);

    this.authService.login(email, probePassword).subscribe({
      next: () => {
        // Astronómicamente improbable: la contraseña aleatoria era la buena. La
        // cuenta existe, que es lo único que interesa averiguar aquí.
        this.checkingEmail = false;
        this.emailConfirmed = true;
        this.showEmailExistsPrompt = true;
      },
      error: (err) => {
        this.checkingEmail = false;
        if (err.status === 404) {
          this.emailConfirmed = true;
          this.showEmailExistsPrompt = false;
        } else if (err.status === 401) {
          this.emailConfirmed = true;
          this.showEmailExistsPrompt = true;
        } else if (err.status === 403) {
          // La cuenta existe pero está sin verificar: se le pedirá la contraseña
          // igual, pero no se puede iniciar sesión con ella para prellenar nada.
          this.emailConfirmed = true;
          this.showEmailExistsPrompt = true;
          this.existingAccountInactive = true;
        } else {
          this.showGenericError = true;
        }
      }
    });
  }

  /**
   * Cuenta ya existente: inicia sesión de verdad con la contraseña que acaba de
   * escribir y prellena sus datos personales, para que no los tenga que volver a
   * teclear. La contraseña se queda en el formulario porque el alta la sigue
   * exigiendo si no hubiera sesión.
   */
  loginExistingAccountAndPrefill(): void {
    const passwordControl = this.signupForm.get('existingAccountPassword')!;
    if (passwordControl.invalid) {
      passwordControl.markAsTouched();
      return;
    }

    this.loadingExistingLogin = true;
    this.showExistingLoginError = false;
    this.showGenericError = false;

    const email = this.signupForm.value.email;

    this.authService.login(email, passwordControl.value).subscribe({
      next: () => {
        // login() ya ha guardado el JWT, así que /auth/me devuelve la cuenta con
        // sus datos (los que tuviera).
        this.authService.getUsersMe().subscribe({
          next: (res) => {
            const user = res?.data?.user || res?.data;
            this.signupForm.patchValue(
              {
                // Solo se prellena lo que la cuenta tenga: si está vacío se deja
                // lo que hubiera escrito el socio, y si no, el campo queda en
                // blanco para que lo complete (sigue siendo obligatorio).
                nombreApellidos: user?.name || this.signupForm.value.nombreApellidos || '',
                nif: user?.nif || this.signupForm.value.nif || '',
                fechaNacimiento: (user?.birthday || '').toString().substring(0, 10)
                  || this.signupForm.value.fechaNacimiento || '',
              },
              { emitEvent: false },
            );
            this.onExistingAccountLogged(user?.email || email);
          },
          // La sesión está iniciada igual: se sigue sin prellenado.
          error: () => this.onExistingAccountLogged(email),
        });
      },
      error: (err) => {
        this.loadingExistingLogin = false;
        if (err.status === 401) {
          this.showExistingLoginError = true;
        } else if (err.status === 403) {
          // Cuenta sin verificar (no detectada al comprobar el email si aquella
          // devolvió 401): no hay inicio de sesión posible, pero el alta sí la
          // acepta, así que se le dejan rellenar los datos a mano.
          this.existingAccountInactive = true;
        } else {
          this.showGenericError = true;
        }
      }
    });
  }

  /**
   * Cambia el usuario de Google por una sesión de Citylok (/auth/google/login) y
   * sigue como cualquier otro inicio de sesión: el abono se creará sobre esa
   * cuenta y ya no hace falta contraseña, porque el backend autoriza con el token.
   *
   * El backend fusiona por email (GoogleServices::findOrCreateUser), así que
   * esto NO crea una cuenta duplicada si ese correo ya tenía una de Citylok:
   * entra en la que ya existía y le engancha el `google_id`. Si no existía, la
   * crea ya activa (Google da el email por verificado).
   */
  private signInWithGoogle(socialUser: SocialUser): void {
    this.loadingGoogle = true;
    this.showGenericError = false;
    this.showExistingLoginError = false;
    this.signupErrorMsg = null;

    this.authService.loginGoogle(socialUser.id, socialUser.idToken).subscribe({
      next: () => {
        this.authService.getUsersMe().subscribe({
          next: (res) => {
            const user = res?.data?.user || res?.data;
            // El email lo manda la cuenta de Google elegida, no lo que el socio
            // hubiera escrito antes en el campo: es la cuenta sobre la que se va
            // a crear el abono y la pantalla lo dice así. emitEvent:false para
            // no disparar el valueChanges de email, que reiniciaría todo.
            this.signupForm.patchValue(
              {
                email: user?.email || socialUser.email || '',
                nombreApellidos: user?.name || this.signupForm.value.nombreApellidos || socialUser.name || '',
                nif: user?.nif || this.signupForm.value.nif || '',
                fechaNacimiento: (user?.birthday || '').toString().substring(0, 10)
                  || this.signupForm.value.fechaNacimiento || '',
              },
              { emitEvent: false },
            );
            this.onGoogleAccountLogged(user?.email || socialUser.email);
          },
          // La sesión está iniciada igual: se sigue sin prellenado.
          error: () => this.onGoogleAccountLogged(socialUser.email),
        });
      },
      error: () => {
        this.loadingGoogle = false;
        this.showGenericError = true;
        this.scrollToAlerts();
      }
    });
  }

  private onGoogleAccountLogged(email: string): void {
    this.loadingGoogle = false;
    this.googleSession = true;
    // Ya se sabe a qué cuenta va el abono, así que se salta la comprobación del
    // email y su rama de contraseña: se pasa directo a los datos personales.
    this.emailConfirmed = true;
    this.showEmailExistsPrompt = false;
    this.existingAccountInactive = false;
    this.onExistingAccountLogged(email);
  }

  private onExistingAccountLogged(email: string): void {
    this.loadingExistingLogin = false;
    this.isLoggedIn = true;
    this.sessionStartedHere = true;
    this.currentUserEmail = email;
  }

  /**
   * Cierre del asistente. Lo que autoriza el alta depende de cómo se haya
   * identificado el socio:
   *  - Con sesión (la traía, la ha iniciado con su contraseña aquí, o ha entrado
   *    con Google): el propio JWT, sin contraseña. Es lo único posible con
   *    Google, cuya contraseña es aleatoria.
   *  - Cuenta que existe pero está sin verificar: su contraseña, porque con esa
   *    cuenta no se puede iniciar sesión (/auth/login devuelve 403 antes de
   *    llegar a comprobarla).
   *  - Email sin cuenta: la contraseña que acaba de crear para la cuenta nueva.
   */
  finishSignup(): void {
    const fields = ['idCategory', 'nombreApellidos', 'nif', 'fechaNacimiento', 'email'];
    // Los campos de repetición solo se piden donde se piden los originales: con
    // sesión iniciada el email no se teclea (lo da la cuenta) y no hay contraseña
    // que crear.
    if (!this.isLoggedIn) {
      fields.push('emailConfirm');
    }
    const needsNewPassword = !this.isLoggedIn && !this.existingAccountInactive;
    if (needsNewPassword) {
      fields.push('password', 'passwordConfirm');
    }
    fields.push('condaccept');

    const fieldsValid = this.validateFields(fields);

    // La butaca no es un control del formulario (se elige en el mapa), así que
    // su falta se avisa igual que la de cualquier campo obligatorio.
    if (!this.selectedSeatId) {
      this.missingFields.push('Butaca');
    }

    if (!fieldsValid || !this.selectedSeatId) {
      this.flashErrorMsg();
      return;
    }

    if (this.isLoggedIn) {
      this.submitSignup(null);
      return;
    }

    if (this.existingAccountInactive) {
      const passwordControl = this.signupForm.get('existingAccountPassword')!;
      if (passwordControl.invalid) {
        passwordControl.markAsTouched();
        return;
      }
      this.submitSignup(passwordControl.value);
      return;
    }

    this.submitSignup(this.signupForm.value.password);
  }

  /**
   * Un único paso contra el backend: resuelve la cuenta (la del token si hay
   * sesión, y si no la del email: valida su contraseña o la crea ya activa), crea
   * el abono pendiente de pago y devuelve un JWT. No hay que registrar ni
   * loguear aparte.
   */
  private submitSignup(password: string | null): void {
    this.showErrorMsg = false;
    this.showGenericError = false;
    this.showExistingLoginError = false;
    this.signupErrorMsg = null;
    this.loadingSignup = true;

    this.signupService.signUp({
      // La que resolvió `context`. Si por lo que sea no llegó, se omite y el
      // servidor la vuelve a resolver.
      ...(this.idSeason ? { id_season: this.idSeason } : {}),
      id_category: this.signupForm.value.idCategory,
      id_seat: this.selectedSeatId!,
      name: this.signupForm.value.nombreApellidos,
      nif: this.signupForm.value.nif,
      email: this.signupForm.value.email,
      birthday: this.signupForm.value.fechaNacimiento,
      // Sin contraseña cuando hay sesión: el interceptor adjunta el JWT y el
      // backend autoriza el alta con él sobre la cuenta del token.
      ...(password ? { password } : {}),
    }).subscribe({
      next: (res) => this.onSignedUp(res.seasonPass, res.auth.access_token),
      error: (err: SeasonPassSignupError) => {
        this.loadingSignup = false;

        // Su cuenta ya tiene un abono de la temporada: el alta NO se ha creado y
        // reintentar no puede funcionar, lo único accionable es ir a verlo (o a
        // pagarlo, si está pendiente).
        if (err.code === 'USER_ALREADY_HAS_PASS') {
          this.userHasOtherPass = true;
          this.signupErrorMsg = err.message || 'Esta cuenta ya tiene un abono para esta temporada.';
          this.scrollToAlerts();
          // Su contraseña ya se ha validado contra la cuenta, así que se guarda
          // el token para que "Ir a mis abonos" no le pida iniciar sesión otra vez.
          if (err.auth) {
            localStorage.setItem('jwt_token', err.auth.access_token);
            this.authService.check(true).subscribe();
          }
          return;
        }

        // La butaca elegida ya no vale (se la han quedado mientras rellenaba el
        // formulario, no está en el mapa vigente, o se ha vendido en un evento):
        // se descarta para forzar que elija otra, porque reenviar la misma
        // volvería a fallar.
        if (err.code === 'SEAT_NOT_AVAILABLE' || err.code === 'SEAT_NOT_IN_SPACE' || err.code === 'SEAT_SOLD_IN_EVENT') {
          this.clearSeat();
          this.signupErrorMsg = err.message || 'Esa butaca ya no está disponible. Elige otra en el mapa.';
          if (err.auth) {
            localStorage.setItem('jwt_token', err.auth.access_token);
            this.authService.check(true).subscribe();
          }
          this.scrollToAlerts();
          return;
        }

        if (err.code === 'INVALID_CREDENTIALS') {
          this.showExistingLoginError = true;
          // El error se muestra bajo el propio campo, no en el aviso de arriba.
          this.scrollIntoView('existing-password-field');
          return;
        }

        // El token con el que se autorizaba el alta ya no valía: reintentar el
        // mismo envío no puede funcionar, hay que volver a identificarse.
        if (err.code === 'SESSION_EXPIRED') {
          localStorage.removeItem('jwt_token');
          this.isLoggedIn = false;
          this.sessionStartedHere = false;
          this.googleSession = false;
          this.emailConfirmed = false;
          this.signupErrorMsg = err.message || 'Tu sesión ha caducado, vuelve a iniciarla.';
          this.scrollToAlerts();
          return;
        }

        this.signupErrorMsg = err.message || 'Ha ocurrido un error, vuelve a intentarlo.';
        this.scrollToAlerts();
      }
    });
  }

  /**
   * El abono ya existe en el servidor, pendiente de pago: se guarda el token de
   * la cuenta y se lleva al socio directo a su checkout, que es el que lanza el
   * pago con Redsys.
   */
  private onSignedUp(seasonPass: SeasonPass, accessToken: string): void {
    localStorage.setItem('jwt_token', accessToken);

    this.authService.check(true).subscribe(() => {
      this.loadingSignup = false;
      // Como socio, no como gestor: /abono-checkout está bajo AuthGuard y el
      // abono es suyo (ver goToMyPasses).
      this.authService.setUserMode('user');
      this.router.navigate(['/abono-checkout', seasonPass.id]);
    });
  }
}
